import axios from "axios";

/**
 * Public payment-link API client.
 *
 * Talks to the wallet-management backend's `/info` endpoint (P3.1 —
 * `functions/paymentLinkInfo/handler.ts`) and the existing 402
 * `/payment-link/{linkId}` endpoint for payment submission.
 *
 * Unlike `mcpApi.js`'s `axiosToken`, this instance:
 *   - Uses `REACT_APP_API_BASE_URL` (custom domain: dev-api /
 *     api.horuswallet.com), NOT the raw API Gateway URL in
 *     `REACT_APP_SOCIAL_WALLET_API`. The /info endpoint + the
 *     payment submission both live behind the custom domain.
 *   - Has NO auth interceptor — /info is public, x402 payment POSTs
 *     use the `X-PAYMENT` header, not Bearer tokens.
 *   - No `Content-Type` default — the 402 endpoint is a GET with
 *     only the `X-PAYMENT` header; adding JSON Content-Type on a
 *     GET can trip some CDN configs.
 */
const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://dev-api.horuswallet.com";

export const paymentLinksAxios = axios.create({
  baseURL: API_BASE,
});

/**
 * Custom error the UI can `instanceof`-check to distinguish the
 * "this slug / code / uuid doesn't exist" 404 from a transient
 * network / 5xx. Pages render a proper NotFoundPage on this error
 * (vs. a retry-friendly "something went wrong" card on others).
 */
export class LinkNotFoundError extends Error {
  constructor(message = "Payment link not found.") {
    super(message);
    this.name = "LinkNotFoundError";
    // Restore prototype chain (same reason as SlugConflictError in
    // the backend — no-op on modern TS targets, insurance for a
    // future downlevel).
    Object.setPrototypeOf(this, LinkNotFoundError.prototype);
  }
}

/**
 * The `/info` 410 body the backend emits when a slug's HNS owner
 * has renamed / released the name. We surface this to the UI so a
 * "this link moved" message can point the user at `currentUrl`.
 * Mirrors the shape in `functions/paymentLinkInfo/handler.ts`.
 */
export class LinkOwnershipChangedError extends Error {
  constructor(body) {
    super(body?.error || "This link's owner changed their username.");
    this.name = "LinkOwnershipChangedError";
    this.oldUsername = body?.old_username || null;
    this.currentUsername = body?.current_username || null;
    this.currentUrl = body?.current_url || null;
    this.fallbackUrl = body?.fallback_url || null;
    this.shortUrl = body?.short_url || null;
    Object.setPrototypeOf(this, LinkOwnershipChangedError.prototype);
  }
}

/**
 * Shared error mapper for all three /info fetchers. Returns the
 * response data on success; throws the narrow error classes above
 * on known failure shapes; throws generic `Error` for everything
 * else so the page can render a retry UI.
 */
async function requestInfo(path) {
  try {
    const res = await paymentLinksAxios.get(path);
    return res.data;
  } catch (err) {
    // axios errors carry the response in `err.response`. We map on
    // status + body before falling through to a generic Error.
    const status = err?.response?.status;
    const body = err?.response?.data;
    if (status === 404) {
      throw new LinkNotFoundError(body?.error || "Payment link not found.");
    }
    if (status === 410 && body?.reason === "owner_username_changed") {
      throw new LinkOwnershipChangedError(body);
    }
    // 410 for expired/disabled/maxed: include the reason so the UI
    // can render a descriptive message (not just "gone").
    if (status === 410) {
      const e = new Error(body?.error || "This payment link is no longer available.");
      e.statusCode = 410;
      e.reason = body?.reason || "gone";
      throw e;
    }
    // Anything else (5xx, network) — surface generically.
    const message =
      body?.error || body?.message || err?.message || "Failed to load payment link.";
    throw new Error(message);
  }
}

export function fetchLinkInfoByCode(code) {
  return requestInfo(`/pay/p/${encodeURIComponent(code)}/info`);
}

export function fetchLinkInfoBySlug(username, slug) {
  // Strip a leading `@` defensively so callers can pass either
  // form. The backend also strips, but doing it here keeps the
  // URL tidy in the network tab.
  const cleanUsername = String(username || "").replace(/^@/, "");
  return requestInfo(
    `/pay/@${encodeURIComponent(cleanUsername)}/${encodeURIComponent(slug)}/info`,
  );
}

export function fetchLinkInfoById(linkId) {
  return requestInfo(`/payment-link/${encodeURIComponent(linkId)}/info`);
}

// ── Invoices (P4) ────────────────────────────────────────────

/**
 * Backend 404 on `/pay/invoice/{publicSlug}/info` — the slug
 * doesn't resolve. Distinct class from LinkNotFoundError so the
 * page renders a tailored "invoice not found" screen instead of
 * the generic payment-link one.
 */
export class InvoiceNotFoundError extends Error {
  constructor(message = 'Invoice not found.') {
    super(message);
    this.name = 'InvoiceNotFoundError';
    Object.setPrototypeOf(this, InvoiceNotFoundError.prototype);
  }
}

/**
 * Fetch the public projection of an invoice from the backend's
 * `/pay/invoice/{publicSlug}/info` endpoint.
 *
 * CRITICAL-1 fix (P4 Codex review): the URL path component is the
 * random capability slug, not the human `INV-001` invoice number.
 * Sequential numbers gave a low-entropy public surface; an 8-char
 * base62 slug raises the guess space to ~62^8 ≈ 2.18×10^14. The
 * human invoice number is still in the response body for display.
 *
 * Returns `{ invoice, paymentLink }` — the invoice carries line
 * items + totals + status; the paymentLink is the existing
 * paymentLinkInfo shape so the webapp's CheckoutPanel renders
 * inline without a second fetch.
 *
 * Errors:
 *   - 404 → `InvoiceNotFoundError` for the NotFoundPage branch.
 *   - Other → plain `Error` with the backend's message.
 */
export async function fetchInvoiceInfo(publicSlug) {
  try {
    const res = await paymentLinksAxios.get(
      `/pay/invoice/${encodeURIComponent(publicSlug)}/info`,
    );
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    const body = err?.response?.data;
    if (status === 404) {
      throw new InvoiceNotFoundError(body?.error || 'Invoice not found.');
    }
    const message =
      body?.error || body?.message || err?.message || 'Failed to load invoice.';
    throw new Error(message);
  }
}

// ── P3.2 v2 — direct-transfer confirmation ───────────────────

/**
 * Backend 409 `tx_already_used` — the tx hash was already used
 * to settle another payment (double-spend protection via the
 * `TX#{hash}` lookup row in DynamoDB).
 */
export class TxAlreadyUsedError extends Error {
  constructor(message) {
    super(message || 'This transaction was already used to settle a payment.');
    this.name = 'TxAlreadyUsedError';
    this.code = 'tx_already_used';
    Object.setPrototypeOf(this, TxAlreadyUsedError.prototype);
  }
}

/**
 * Build the /confirm-direct URL for a link regardless of which
 * route shape the page was reached through. We reuse the link's
 * `urls.full` (UUID form on the API host) as a stable, always-
 * present handle — short + slug URLs live on the webapp host and
 * would need a rewrite, while the UUID URL is API-hosted so we
 * can suffix `/confirm-direct` directly.
 */
function resolveConfirmDirectUrl(link) {
  if (link?.urls?.full) {
    return `${link.urls.full}/confirm-direct`;
  }
  // Defensive fallback if /info ever stops returning `urls.full`.
  const base =
    process.env.REACT_APP_API_BASE_URL || 'https://dev-api.horuswallet.com';
  return `${base}/payment-link/${link.linkId}/confirm-direct`;
}

/**
 * POST a direct-transfer confirmation to the backend. The handler
 * verifies the tx on-chain, validates the payer's signed
 * attestation (CRITICAL-fix), and atomically increments uses +
 * writes the receipt. Returns the receipt body on 200.
 *
 * `signature` is a 65-byte EIP-191 `personal_sign` over the
 * attestation message (built on both sides by `buildAttestationMessage`).
 * `expiresAt` is the ms-since-epoch expiry of that attestation
 * (must be within 30 min of `Date.now()` per the backend's
 * ATTESTATION_VALIDITY_MS). Without the signature the backend
 * returns 400 `missing_signature`; with a signature that doesn't
 * recover to `payerAddress` it returns 400 `signature_mismatch`.
 *
 * Response branches:
 *   - 200: `{ success, receipt }` — payment recorded.
 *   - 202: `{ retry: true, confirmations?, required? }` — polling signal.
 *   - 400: bad params / on-chain mismatch / signature mismatch.
 *   - 409 `tx_already_used`: throws `TxAlreadyUsedError`.
 *   - 409 `link_${status}`: throws Error with `.reason` set.
 *   - 410 `owner_username_changed`: throws `LinkOwnershipChangedError`.
 *   - 5xx: throws a generic Error.
 */
export async function confirmDirectPayment(
  link,
  txHash,
  payerAddress,
  signature,
  expiresAt,
) {
  const url = resolveConfirmDirectUrl(link);
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ txHash, payerAddress, signature, expiresAt }),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // Body wasn't JSON (gateway error) — we still want to
    // surface the status below.
  }

  if (res.status === 200) {
    return data;
  }

  // 202 retry — backend says "tx not yet confirmed, try again".
  // The hook handles this as a non-error polling signal by
  // inspecting `err.retry`.
  if (res.status === 202 && data && data.retry) {
    const err = new Error(data.error || 'Awaiting on-chain confirmations.');
    err.retry = true;
    err.reason = data.reason || 'awaiting_confirmations';
    err.confirmations = data.confirmations;
    err.required = data.required;
    throw err;
  }

  if (res.status === 409 && data?.error === 'tx_already_used') {
    throw new TxAlreadyUsedError(data.message);
  }

  if (res.status === 410 && data?.reason === 'owner_username_changed') {
    throw new LinkOwnershipChangedError(data);
  }

  const message =
    data?.error ||
    data?.message ||
    `Failed to confirm payment (HTTP ${res.status}).`;
  const err = new Error(message);
  err.statusCode = res.status;
  err.reason = data?.reason;
  throw err;
}
